import { useState, useMemo, useCallback } from 'react';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useRentalHistory, useOrderDetail } from '../../hooks/useStylistPerformance.js';
import type { RentalHistoryItem, OrderDetailItem } from '../../types/stylist-performance.js';

/** @graph-connects none */
const PAGE_SIZE = 20;

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business ★4段階レーティング表示コンポーネント。gold=評価値、gray=残り。
 * @graph-connects none
 */
function Stars({ rating, label }: { rating: number | null; label: string }) {
  if (rating === null) return <span className="text-xs text-[var(--color-text-muted)]">—</span>;
  const clamped = Math.max(0, Math.min(4, rating));
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <span className="text-[var(--color-text-muted)]">{label}</span>
      <span className="text-sm">
        <span className="text-amber-500">{'★'.repeat(clamped)}</span>
        <span className="text-gray-300">{'★'.repeat(4 - clamped)}</span>
      </span>
    </span>
  );
}

/** @graph-connects none */
const SIZE_LABELS: Record<number, { text: string; className: string }> = {
  1: { text: 'SMALL', className: 'text-[var(--color-error)] border-[var(--color-error)]' },
  2: { text: 'FIT', className: 'text-[var(--color-success)] border-[var(--color-success)]' },
  3: { text: 'LARGE', className: 'text-blue-500 border-blue-500' },
};

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 評価バッジ（GOOD=緑、BAD=赤、未評価=グレー）。
 * @graph-connects none
 */
function EvalBadge({ evaluation }: { evaluation: 'good' | 'bad' | null }) {
  if (evaluation === 'good')
    return (
      <span className="inline-block rounded px-2 py-0.5 text-[11px] font-semibold text-[var(--color-success)] bg-emerald-50">
        GOOD
      </span>
    );
  if (evaluation === 'bad')
    return (
      <span className="inline-block rounded px-2 py-0.5 text-[11px] font-semibold text-[var(--color-error)] bg-red-50">
        BAD
      </span>
    );
  return (
    <span className="inline-block rounded px-2 py-0.5 text-[11px] font-semibold text-[var(--color-text-muted)] bg-gray-100">
      未評価
    </span>
  );
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business オーダー詳細モーダル。アイテム一覧・★評価・サイズ感・スタイリストコメント・再選定履歴を表示。
 * @graph-connects useOrderDetail [calls] オーダー詳細API
 */
function OrderDetailModal({ orderId, onClose }: { orderId: number; onClose: () => void }) {
  const { data, isLoading } = useOrderDetail(orderId);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative max-h-[80vh] w-[90%] max-w-[640px] overflow-y-auto rounded-lg bg-[var(--color-bg-raised)] p-6 shadow-xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-lg text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
        >
          ×
        </button>

        {isLoading ? (
          <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
            読み込み中...
          </div>
        ) : data ? (
          <>
            <h3 className="text-base font-bold text-[var(--color-text-primary)]">
              オーダー #{data.phoenixOrderId}
            </h3>
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">
              {data.stylistName} &nbsp;|&nbsp; {data.styledDate} 完了 &nbsp;|&nbsp;{' '}
              <EvalBadge evaluation={data.evaluation} />
            </p>

            <div className="mt-4 space-y-0 divide-y divide-[var(--color-border)]">
              {data.items.map((item: OrderDetailItem) => (
                <div key={item.phoenixItemSkuId} className="flex gap-3 py-3">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={`SKU ${String(item.phoenixItemSkuId)}`}
                      className="h-[70px] w-[56px] shrink-0 rounded object-cover"
                    />
                  ) : (
                    <div className="flex h-[70px] w-[56px] shrink-0 items-center justify-center rounded bg-[var(--color-bg-base)] text-[10px] text-[var(--color-text-muted)]">
                      SKU
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="mt-0.5 text-[11px] text-[var(--color-text-muted)]">
                      {[item.color, item.size].filter(Boolean).join(' | ')}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      {item.sizeFeeling !== null && SIZE_LABELS[item.sizeFeeling] && (
                        <span
                          className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${SIZE_LABELS[item.sizeFeeling]?.className ?? ''}`}
                        >
                          {SIZE_LABELS[item.sizeFeeling]?.text}
                        </span>
                      )}
                      <Stars rating={item.colorRating} label="カラー" />
                      <Stars rating={item.designRating} label="デザイン" />
                      {item.isUsed === 'yes' && (
                        <span className="rounded border border-[var(--color-success)] px-1.5 py-0.5 text-[11px] text-[var(--color-success)]">
                          着用済み
                        </span>
                      )}
                      {item.isUsed === 'no' && (
                        <span className="rounded border border-[var(--color-text-muted)] px-1.5 py-0.5 text-[11px] text-[var(--color-text-muted)]">
                          未着用
                        </span>
                      )}
                      {item.isUserPicked && (
                        <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold text-blue-600">
                          ユーザー自選
                        </span>
                      )}
                      {item.isPurchased && (
                        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">
                          購入済み
                        </span>
                      )}
                    </div>
                    {item.comment && (
                      <p className="mt-1 text-xs text-[var(--color-text-secondary)] italic">
                        {item.comment}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {data.stylistComment && (
              <div className="mt-4 rounded-md bg-[var(--color-accent-bg,rgba(59,88,120,0.08))] p-3">
                <p className="mb-1 text-[11px] font-semibold tracking-wider text-[var(--color-accent-primary)]">
                  STYLIST COMMENT
                </p>
                <p className="text-[13px] leading-relaxed text-[var(--color-text-primary)]">
                  {data.stylistComment}
                </p>
              </div>
            )}

            {data.rejectReasons.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-[11px] font-semibold tracking-wider text-[var(--color-error)]">
                  再選定履歴
                </p>
                {data.rejectReasons.map((r, i) => (
                  <div
                    key={i}
                    className="mb-1 rounded border border-[var(--color-border)] px-3 py-2 text-xs"
                  >
                    <span className="font-semibold text-[var(--color-text-secondary)]">
                      {r.rejectBy}
                    </span>
                    : {r.reason}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
            データが見つかりません
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 振り返りタブ。レンタル履歴一覧（フィルタ・ソート・ページネーション）とオーダー詳細モーダル。
 * @graph-connects useRentalHistory [calls] 履歴一覧API
 */
export function HistoryTab({ stylistId }: { stylistId: string | undefined }) {
  /** @graph-connects none */
  const [evaluation, setEvaluation] = useState('');
  /** @graph-connects none */
  const [selector, setSelector] = useState('');
  /** @graph-connects none */
  const [hasPurchase, setHasPurchase] = useState('');
  /** @graph-connects none */
  const [sortBy, setSortBy] = useState('styled_date');
  /** @graph-connects none */
  const [page, setPage] = useState(0);
  /** @graph-connects none */
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  const params = useMemo(
    () => ({
      ...(stylistId ? { stylistId } : {}),
      ...(evaluation ? { evaluation } : {}),
      ...(selector ? { selector } : {}),
      ...(hasPurchase ? { hasPurchase } : {}),
      sortBy,
      sortOrder: 'desc' as const,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [stylistId, evaluation, selector, hasPurchase, sortBy, page],
  );

  const { data, isLoading } = useRentalHistory(params);

  const resetPage = useCallback(() => {
    setPage(0);
  }, []);

  /** @graph-connects none */
  const selectCls =
    'rounded-md border border-[var(--color-border)] bg-[var(--color-bg-raised)] px-2 py-1 text-xs text-[var(--color-text-primary)] outline-none';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <label className="text-[11px] font-semibold text-[var(--color-text-muted)]">評価</label>
          <select
            className={selectCls}
            value={evaluation}
            onChange={(e) => {
              setEvaluation(e.target.value);
              resetPage();
            }}
          >
            <option value="">すべて</option>
            <option value="good">GOOD</option>
            <option value="bad">BAD</option>
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-[11px] font-semibold text-[var(--color-text-muted)]">選定者</label>
          <select
            className={selectCls}
            value={selector}
            onChange={(e) => {
              setSelector(e.target.value);
              resetPage();
            }}
          >
            <option value="">すべて</option>
            <option value="stylist">スタイリスト選定のみ</option>
            <option value="user_picked">ユーザー自選あり</option>
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-[11px] font-semibold text-[var(--color-text-muted)]">購入</label>
          <select
            className={selectCls}
            value={hasPurchase}
            onChange={(e) => {
              setHasPurchase(e.target.value);
              resetPage();
            }}
          >
            <option value="">すべて</option>
            <option value="true">購入あり</option>
            <option value="false">購入なし</option>
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-[11px] font-semibold text-[var(--color-text-muted)]">ソート</label>
          <select
            className={selectCls}
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
            }}
          >
            <option value="styled_date">スタイリング日 ↓</option>
            <option value="returned_date">返却日 ↓</option>
          </select>
        </div>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="py-8 text-center text-sm text-[var(--color-text-muted)]">
            読み込み中...
          </div>
        ) : data && data.items.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-base)]">
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                      オーダー
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                      スタイリスト
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                      完了日
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                      返却日
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                      件数
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                      評価
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                      タグ
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item: RentalHistoryItem) => (
                    <tr
                      key={item.phoenixOrderId}
                      className="cursor-pointer border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]"
                      onClick={() => {
                        setSelectedOrderId(item.phoenixOrderId);
                      }}
                    >
                      <td className="px-3 py-2 tabular-nums">
                        <a
                          href={`https://phoenix-styling.air-closet.biz/users/${String(item.arthurUserId)}/karte`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-[var(--color-accent-primary)] hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          #{item.phoenixOrderId}
                        </a>
                      </td>
                      <td className="px-3 py-2">{item.stylistName}</td>
                      <td className="px-3 py-2 tabular-nums">{item.styledDate}</td>
                      <td className="px-3 py-2 tabular-nums">{item.returnedDate ?? '—'}</td>
                      <td className="px-3 py-2 tabular-nums">{item.itemCount}</td>
                      <td className="px-3 py-2">
                        <EvalBadge evaluation={item.evaluation} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          {item.hasPurchase && (
                            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">
                              購入
                            </span>
                          )}
                          {item.hasUserPicked && (
                            <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold text-blue-600">
                              自選あり
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-text-muted)]">
              <span>
                {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, data.totalCount)} /{' '}
                {data.totalCount}件
              </span>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => {
                    setPage((p) => Math.max(0, p - 1));
                  }}
                  className="rounded border border-[var(--color-border)] px-3 py-1 text-xs disabled:opacity-40"
                >
                  前へ
                </button>
                <button
                  type="button"
                  disabled={(page + 1) * PAGE_SIZE >= data.totalCount}
                  onClick={() => {
                    setPage((p) => p + 1);
                  }}
                  className="rounded border border-[var(--color-border)] px-3 py-1 text-xs disabled:opacity-40"
                >
                  次へ
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="py-8 text-center text-sm text-[var(--color-text-muted)]">
            該当するオーダーはありません
          </div>
        )}
      </Card>

      {selectedOrderId !== null && (
        <OrderDetailModal
          orderId={selectedOrderId}
          onClose={() => {
            setSelectedOrderId(null);
          }}
        />
      )}
    </div>
  );
}
